import { Button } from '@lightspeed/design-system-react'
import React, { useState } from 'react'
import { useT } from '@transifex/react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../../../state/hooks'
import {
  selectSaleFulfilmentDetail,
  setSaleFulfilmentDetail,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  AddressErrorsStatus,
  useLineItemFulfillmentAddressDetails,
} from '../popover/hooks/useLineItemFulfillmentAddressDetails'
import { FulfilmentType } from '../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../../constants'
import { AddOrEditAddressButton, EditAddressButtons } from './EditAddressButton'

interface FormButtonOnEmptyShippingAddressProps {
  loading?: boolean
  handleEmptyShippingCorrectBillingAddress?: () => void
  onClose?: () => void
}

export const FormButtonOnEmptyShippingAddress: React.FC<FormButtonOnEmptyShippingAddressProps> = ({
  loading,
  handleEmptyShippingCorrectBillingAddress,
  onClose,
}) => {
  const t = useT()
  const fulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const { error, status } = useLineItemFulfillmentAddressDetails(
    fulfilmentDetail
  )
  const dispatch = useDispatch()
  const saleManager = useSaleManager()
  const [isClickedToOpenModal, setIsClickedToOpenModal] = useState(false)
  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const isIncorrectBillingAndNotEmptyShippingAddress =
    status === AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING

  const copyBillingAddressToShippingAddress = () => {
    const newFulfillmentDetail = {
      type: FulfilmentType.DELIVERY,
      note: fulfilmentDetail?.note || '',
      billingAddress: fulfilmentDetail?.billingAddress,
      shippingAddress:
        fulfilmentDetail?.billingAddress || fulfilmentDetail?.shippingAddress,
    }

    dispatch(setSaleFulfilmentDetail(newFulfillmentDetail))

    if (isCaptureAddressSellScreenEnabled)
      saleManager.updateFulfillmentDetails([newFulfillmentDetail])
  }

  return (
    <>
      <AddOrEditAddressButton
        variant={
          isIncorrectBillingAndNotEmptyShippingAddress ? 'go' : 'supplementary'
        }
        type={
          isIncorrectBillingAndNotEmptyShippingAddress
            ? EditAddressButtons.edit
            : EditAddressButtons.add
        }
        hasError={Boolean(error)}
        text={
          isIncorrectBillingAndNotEmptyShippingAddress
            ? t('Update address', {
                _context: 'Popover button',
              })
            : t('Add new address', {
                _context: 'Modal button',
              })
        }
        onClose={onClose}
      />
      {status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING && (
        <Button
          onClick={() => {
            copyBillingAddressToShippingAddress()
            handleEmptyShippingCorrectBillingAddress &&
              handleEmptyShippingCorrectBillingAddress()
            onClose?.()
          }}
          variant="go"
          loading={loading}
          data-track="use-billing-address-for-empty-shipping-address"
        >
          {t('Use billing address for shipping', {
            _context: 'Modal button',
          })}
        </Button>
      )}
      {status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING &&
        (!isClickedToOpenModal ? (
          <Button
            onClick={() => setIsClickedToOpenModal(true)}
            variant="go"
            loading={loading}
            data-track="use-billing-address-for-empty-shipping-address"
          >
            {t('Update billing address', {
              _context: 'Modal button',
            })}
          </Button>
        ) : (
          <AddOrEditAddressButton
            type="edit"
            hasError={Boolean(error)}
            onClick={() => {
              copyBillingAddressToShippingAddress()
            }}
            onClose={onClose}
            variant="go"
            text={t('Update billing address', {
              _context: 'Modal button',
            })}
            isModalOpeningOnError={true}
          />
        ))}
    </>
  )
}
